/**
 * Fait : répond à la seule question que la personne se pose devant l'écran de
 *   l'argent — « est-ce que ma prochaine demande partira, et de combien
 *   est-ce que je dispose ? » Elle croise les DEUX plafonds, parce que le
 *   serveur les croise aussi.
 *
 *   POURQUOI CE MODULE EXISTE, et il est né d'un défaut réel trouvé par une
 *   épreuve indépendante le 2026-08-25 : l'écran affichait « Mon plafond :
 *   50,00 €. Il me reste 50,00 € » pendant que le plafond de la famille valait
 *   zéro — donc pendant que RIEN ne pouvait partir. La règle générale était
 *   bien écrite juste en dessous (« c'est toujours le plus bas des deux qui
 *   arrête »), mais en texte atténué, et une règle générale n'est pas un état
 *   présent : à fort grossissement on lit le chiffre et pas la nuance.
 *
 *   LA RÈGLE EST CELLE DU SERVEUR, relue dans `economie/budget.ts` : la
 *   réservation exige `plafondCentimes > 0` sur le budget global PUIS sur
 *   celui du membre. Un plafond à zéro — ou jamais posé, ce qui revient au
 *   même — ferme donc tout, y compris la voie par abonnement qui ne coûte
 *   rien. Ce n'est pas « un petit budget », c'est une porte fermée.
 *
 * Ne fait pas : il n'appelle rien, n'affiche rien, et ne connaît pas le coût
 *   d'une demande — il dit ce qui reste, jamais si telle question passera.
 */

/** Un budget, réduit à ce dont ce calcul a besoin. */
export interface BudgetLu {
  readonly plafondCentimes: number;
  readonly engageCentimes: number;
  readonly consommeCentimes: number;
}

/**
 * Ce qui peut réellement partir. Quand c'est fermé, la CAUSE est nommée : dire
 * « rien ne peut partir » sans dire d'où vient le blocage laisse la personne
 * chercher dans le mauvais écran.
 */
export type CeQuiReste =
  | { readonly ouvert: false; readonly cause: 'famille' | 'moi' | 'les-deux' }
  | { readonly ouvert: true; readonly resteCentimes: number; readonly borne: 'famille' | 'moi' };

/** Ce qu'un budget laisse encore passer, jamais négatif. */
function disponible(budget: BudgetLu): number {
  return Math.max(0, budget.plafondCentimes - budget.consommeCentimes - budget.engageCentimes);
}

/** Croise le plafond de la famille et le mien, dans l'ordre du serveur. */
export function ceQuiReste(global: BudgetLu, mien: BudgetLu): CeQuiReste {
  const familleFermee = global.plafondCentimes <= 0;
  const moiFerme = mien.plafondCentimes <= 0;

  if (familleFermee && moiFerme) return { ouvert: false, cause: 'les-deux' };
  if (familleFermee) return { ouvert: false, cause: 'famille' };
  if (moiFerme) return { ouvert: false, cause: 'moi' };

  const parLaFamille = disponible(global);
  const parMoi = disponible(mien);

  /*
   * À ÉGALITÉ, C'EST LA FAMILLE QUI EST NOMMÉE. Le serveur éprouve le budget
   * global en premier : c'est donc lui qui refuserait, et la personne doit
   * savoir vers qui se tourner.
   */
  return parLaFamille <= parMoi
    ? { ouvert: true, resteCentimes: parLaFamille, borne: 'famille' }
    : { ouvert: true, resteCentimes: parMoi, borne: 'moi' };
}
